import { Translation } from '@modeso/aumico-lib-core-fe';
import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import { ItemType, ITranslationEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';
import { GapReportingVisitor } from './GapReportingVisitor';
import { ITranslatorVisitor } from './ITranslatorVisitor.interface';
import { UntranslatedTexts } from './UntranslatedTexts';
import { CashFlow } from './classes/CashFlow';

export class CashFlowTranslatorVisitor extends GapReportingVisitor implements ITranslatorVisitor {
    constructor(
        private readonly targetLanguage: string,
        private readonly reportLanguage: string,
        private readonly translatableItems: Map<string, Translation>,
        private readonly i18Next: I18NextService,
        untranslated?: UntranslatedTexts,
        translations?: Record<string, ITranslationEntry>,
    ) {
        super(untranslated, 'cashflow', translations);
    }

    visit(cashFlow: CashFlow): void {
        const { cashFlowStatement } = cashFlow;

        cashFlowStatement.financingActivities.entries.forEach((entry) => {
            entry.name = this.getTranslation(entry.fliId, entry.name);
        });
        cashFlowStatement.financingActivities.title = this.resolveEditableLabel(
            'aumico_annual_report_cash_flow_financing_activities_title',
            cashFlowStatement.financingActivities.title,
        );

        cashFlowStatement.investingActivities.entries.forEach((entry) => {
            if (entry.parentFli != null && entry.investing) {
                // A custom parent's child carries a user-typed name; the standard one still matches the
                // report-language label. `isCustom` is not reliable on the saved entry, so decide by the
                // name itself — same isEdited path as the titles.
                entry.name = this.resolveEditableLabel(
                    'aumico_annual_report_cash_flow_investing_activities_investments',
                    entry.name,
                );
            } else if (entry.parentFli != null && !entry.investing) {
                entry.name = this.resolveEditableLabel(
                    'aumico_annual_report_cash_flow_investing_activities_disinvestments',
                    entry.name,
                );
            } else {
                entry.name = this.getTranslation(entry.fliId, entry.name);
            }
        });
        cashFlowStatement.investingActivities.title = this.resolveEditableLabel(
            'aumico_annual_report_cash_flow_investing_activities_title',
            cashFlowStatement.investingActivities.title,
        );

        cashFlowStatement.operatingActivities.entries.forEach((entry) => {
            entry.name = this.getTranslation(entry.fliId, entry.name);
        });

        cashFlowStatement.operatingActivities.title = this.resolveEditableLabel(
            'aumico_annual_report_cash_flow_operating_activities_title',
            cashFlowStatement.operatingActivities.title,
        );

        cashFlowStatement.total.total1 = this.getI18NextLocalizedText(
            'aumico_annual_report_cash_flow_total_total1_name',
        );
        cashFlowStatement.total.total2 = this.getI18NextLocalizedText(
            'aumico_annual_report_cash_flow_total_total2_name',
        );
    }

    private getTranslation(fliId: string, currentName: string): string {
        const name = `${fliId}_${ItemType.FLI}`;
        const translations = this.translatableItems.get(name);
        const translated = translations != null ? translations[this.targetLanguage] ?? EMPTY_STRING : EMPTY_STRING;

        return this.resolve(currentName, translated);
    }

    private getI18NextLocalizedText(key: string): string {
        return this.i18Next.t(key, {
            lng: this.targetLanguage,
        }) as string;
    }

    /**
     * An editable section title. If the user renamed it — it differs from the standard label in the
     * report language — it is real report text: translate via the block or report it as a gap. If it
     * still matches the standard, use the target-language i18n label. Same shape as the AoP visitor.
     */
    private resolveEditableLabel(key: string, current: string): string {
        const standardInReportLanguage = this.i18Next.t(key, { lng: this.reportLanguage }) as string;
        const isEdited = current !== standardInReportLanguage;

        return this.resolve(current, isEdited ? EMPTY_STRING : this.getI18NextLocalizedText(key));
    }
}
